import * as Yup from "yup";
import { useGetHotelStaticData } from "./useGetHotelStaticData";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRecentSearch } from "./useRecentSearch";
import { useNavigate } from "react-router-dom";
import moment from "moment";

const formSchema = Yup.object({
  checkinDate: Yup.date()
    .transform((curr, orig) => orig ? curr : undefined)
    .required("Check in date is required")
    .typeError("Check in date is required"),
  checkoutDate: Yup.date()
    .transform((curr, orig) => orig ? curr : undefined)
    .required("Check out date is required")
    .typeError("Check out date is required"),
  noOfAdult: Yup.number().required().min(1),
  noOfChild: Yup.number().optional(),
  noOfRooms: Yup.number().required().min(1),
  childAges: Yup.array().optional(),
  city: Yup.mixed().test('city-or-keyword', 'Please select city or hotel', function(value) {
    const { keyword } = this.parent;
    if (keyword && keyword.trim().length > 0) {
      return true;
    }
    return value && (value.value || value.label);
  }),
  nationalityValue: Yup.string().required("Please select nationality"),
  currency: Yup.string().optional(),
});

export function dividePeople(noOfRooms, noOfAdult, noOfChild, childAges) {
  const result = [];
  let childIndex = 0;

  for (let i = 0; i < noOfRooms; i++) {
    const adultsInRoom = Math.floor(noOfAdult / noOfRooms) + (i < noOfAdult % noOfRooms ? 1 : 0); 
    const childrenInRoom = Math.floor(noOfChild / noOfRooms) + (i < noOfChild % noOfRooms ? 1 : 0);
    const childrenAges = childAges.slice(
      childIndex,
      childIndex + childrenInRoom
    ).map(age => !age ? '1' : age);    
    childIndex += childrenInRoom;
    result.push({
      room: i + 1,
      noOfAdult: adultsInRoom,
      noOfChild: childrenInRoom,
      childAges: childrenAges,
    });
  }
  return result;
}

export const useSearchHotels = (queryparams, searchUpdate = false) => {
  const navigate = useNavigate();

  const { details: staticData } = useGetHotelStaticData();
  const { save, loading } = useRecentSearch();
  const [personError,setPersonError]=useState('')
  const [sameDateError,setSameDateError]=useState('')

  const formRef = useRef();
  const formSet = useRef(false);
  const nationalitySet = useRef(false);
  const geoNationalitySet = useRef(false);

  const countries = useMemo(() => {
    return staticData?.countries?.map((items) => ({
      value: items.id,
      label: items.name,
      code : items?.code
    }));
  }, [staticData?.countries?.length]);

  useEffect(() => {
    if (queryparams?.cityValueExists) {
      formRef.current.setFieldValue(
        "cityValueExists",
        queryparams?.cityValueExists
      );
    }
    if (queryparams?.hid) {
      formRef.current.setFieldValue(
        "hid",
        queryparams?.hid
      );
    }
    if (queryparams?.keyword) {
      formRef.current.setFieldValue(
        "keyword",
        queryparams?.keyword
      );
    }
    // if (queryparams?.city) {
    //   formRef.current.setFieldValue(
    //     "city",
    //     queryparams?.city
    //   );
    // }
  }, [formRef.current, queryparams?.cityValueExists, queryparams?.hid, queryparams?.keyword, queryparams?.city]);

  // Auto-detect nationality from IP geolocation, only when no URL-based nationality is present.
  // Falls back to the UAE default silently if detection fails or country isn't in the list.
  useEffect(() => {
    if (queryparams?.nationality) return; // URL param takes priority — handled by the effect below
    if (!countries?.length) return;
    if (geoNationalitySet.current) return;
    geoNationalitySet.current = true;

    const applyCountryCode = (code) => {
      if (!code || !formRef.current) return;
      const matched = countries.find((c) => c.code === code);
      if (matched) {
        formRef.current.setFieldValue("nationality", matched);
        formRef.current.setFieldValue("nationalityValue", matched.value);
      }
    };

    // Use cached result if still within 24 hours
    try {
      const cached = JSON.parse(localStorage.getItem("hw_geo_country") || "null");
      if (cached?.code && cached?.ts && Date.now() - cached.ts < 86_400_000) {
        applyCountryCode(cached.code);
        return;
      }
    } catch (_) {}

    // Fetch country code from IP geolocation (no API key required)
    fetch("https://api.country.is/")
      .then((res) => res.json())
      .then((data) => {
        const code = data?.country;
        if (code) {
          try {
            localStorage.setItem("hw_geo_country", JSON.stringify({ code, ts: Date.now() }));
          } catch (_) {}
          applyCountryCode(code);
        }
      })
      .catch(() => {
        // Silently fail — UAE default remains
      });
  }, [countries, queryparams?.nationality]);

  useEffect(() => {
    if (formRef.current && countries?.length > 0 && queryparams?.nationality && !nationalitySet.current) {
      const nationality = countries?.find?.(
        (c) => c?.value === queryparams?.nationality
      );
      if (nationality) {
        formRef.current.setFieldValue("nationality", nationality);
        formRef.current.setFieldValue("nationalityValue", nationality?.value);
        nationalitySet.current = true;
      }
    }
  }, [countries, queryparams?.nationality]);

  useEffect(() => {
    if (queryparams?.roomInfo && !formSet.current) {
      formSet.current = true;
      formRef.current.setFieldValue("noOfRooms", queryparams?.roomInfo?.length);
      formRef.current.setFieldValue(
        "noOfAdult",
        queryparams?.roomInfo?.reduce((acc, i) => {
          acc = acc + +i?.noOfAdult;
          return acc;
        }, 0)
      );
      formRef.current.setFieldValue(
        "noOfChild",
        queryparams?.roomInfo?.reduce((acc, i) => {
          acc = acc + +i?.noOfChild;
          return acc;
        }, 0)
      );
      formRef.current.setFieldValue(
        "childAges",
        queryparams?.roomInfo?.reduce((acc, i) => {
          acc = [...acc, ...(i?.childAges ?? [])];
          return acc;
        }, [])
      );
    }
  }, [queryparams?.roomInfo?.length, formRef.current]);

  const onSearch = (values) => {
    if (values?.noOfAdult < values?.noOfRooms) {
      setPersonError("Add at least 1 adult per room")
      return
    }
    if (values?.noOfRooms > 9) {
      setPersonError("Maximum number of rooms is 9");
      return;
    }
    if (values?.noOfAdult > values?.noOfRooms * 6) {
      setPersonError("Maximum 6 adults per room");
      return;
    }
    if (values?.noOfChild > values?.noOfRooms * 4) {
      setPersonError("Maximum 4 children per room");
      return;
    }
    const totalGuests = (values?.noOfAdult || 0) + (values?.noOfChild || 0);
    if (totalGuests > values?.noOfRooms * 10) {
      setPersonError("Maximum 10 guests (adults + children) per room");
      return;
    }
    setPersonError("")
    const checkin = new Date(values?.checkinDate);
    const checkout = new Date(values?.checkoutDate);
    const checkinDateOnly = checkin.toISOString().slice(0, 10);
    const checkoutDateOnly = checkout.toISOString().slice(0, 10);
    if(checkinDateOnly === checkoutDateOnly){
      setSameDateError("Checkout date must be after checkin date")
      return
    }
    setPersonError("")
    setSameDateError("")
    save({
      searchData: values,
    });
    const searchData = encodeURIComponent(
      btoa(
        unescape(
          encodeURIComponent(JSON.stringify(values?.city?.details))
        )
      )
    );
    const roomInfo = dividePeople(
      values?.noOfRooms,
      values?.noOfAdult,
      values?.noOfChild,
      values?.childAges
    );
    const risParam = roomInfo
      ?.map(
        (r) =>
          `${r?.noOfAdult}n${r?.noOfChild}${
            r?.childAges?.length > 0
              ? `n${r?.childAges?.map((d) => (!d ? 1 : d))?.join("a")}`
              : ""
          }`
      )
      .join("_");
    if (searchUpdate && !values?.cityChanged) {
      const params = new URLSearchParams(window.location.search);
      params.set("checkinDate", moment(values.checkinDate).format("YYYY-MM-DD"));
      params.set("checkoutDate", moment(values.checkoutDate).format("YYYY-MM-DD"));
      params.set("ris", risParam);
      if (values?.nationality?.value) params.set("nationality", values.nationality.value);
      if (values?.nationality?.code) params.set("residence", values.nationality.code);
      
      if (values?.city) {
        if (!values?.city?.isCity) {
          const cityId =
            values?.city?.details?.rateHawkCityCode ??
            values?.city?.details?.cityId ??
            staticData?.cities?.find(
              (c) => c?.name === values?.city?.details?.cityName
            )?.code;
          params.set("cid", cityId || values?.cityValueExists || "");
          params.set("hid", values?.city?.value || "");
          params.set("isH", "true");
        } else {
          params.set("cid", values?.city?.value || values?.cityValueExists || "");
          params.set("hid", "");
          params.set("isH", "false");
        }
        if (values?.city?.label) {
          params.set("keyword", values?.city?.label);
        }
        if (values?.city?.details) {
          params.set("searchData", searchData);
        }
      }

      navigate(`${window.location.pathname}?${params.toString()}`, {
        replace: true,
        state: new Date().getTime(),
      });
      return;
    }
    if (!values?.city?.isCity) {
      const cityId =
        values?.city?.details?.rateHawkCityCode ??
        values?.city?.details?.cityId ??
        staticData?.cities?.find(
          (c) => c?.name === values?.city?.details?.cityName
        )?.code;
      const url = `/hotels/list?checkinDate=${moment(
        values.checkinDate
      ).format("YYYY-MM-DD")}&checkoutDate=${moment(values.checkoutDate).format(
        "YYYY-MM-DD"
      )}&cid=${cityId || values?.cityValueExists}&hid=${
        !values?.city?.isCity ? values?.city?.value : ""
      }&isH=${!values?.city?.isCity ? true : false}&ris=${roomInfo
        ?.map(
          (r) =>
            `${r?.noOfAdult}n${r?.noOfChild}${
              r?.childAges?.length > 0
                ? `n${r?.childAges?.map((d) => (!d ? 1 : d))?.join("a")}`
                : ""
            }`
        )
        .join("_")}&nationality=${values?.nationality?.value}${values?.city?.label ? `&keyword=${values?.city?.label}` : values?.keyword ? `&keyword=${values?.keyword}` : ''}&searchData=${searchData}&residence=${values?.nationality?.code}`;
      navigate(url, {
        state: new Date().getTime(),
      });
      return;
    }

    const url = `/hotels/list?checkinDate=${moment(values.checkinDate).format(
      "YYYY-MM-DD"
    )}&checkoutDate=${moment(values.checkoutDate).format("YYYY-MM-DD")}&cid=${
      (values?.city?.isCity ? values?.city?.value : "") ||
      values?.cityValueExists
    }&hid=${!values?.city?.isCity ? values?.city?.value : ""}&isH=${!values?.city?.isCity ? true : false}&ris=${roomInfo
      ?.map(
        (r) =>
          `${r?.noOfAdult}n${r?.noOfChild}${
            r?.childAges?.length > 0
              ? `n${r?.childAges?.map((d) => (!d ? 1 : d))?.join("a")}`
              : ""
          }`
      )
      .join("_")}&nationality=${values?.nationality?.value}${values?.city?.label ? `&keyword=${values?.city?.label}` : values?.keyword ? `&keyword=${values?.keyword}` : ''}&searchData=${searchData}&residence=${values?.nationality?.code}`;
    navigate(url, {
      state: new Date().getTime(),
    });
  };

  const initialValues = {
    nationality: {
    "value": "88",
    "label": "United Arab Emirates",
    "code": "AE"},
    countryOfResidence: "AE",
    nationalityValue: "88",
    countryOfResidenceValue: "AE",
    ratings: [],
    city: "",
    cityValueExists: "",
    hid: "",
    checkinDate: null,
    checkoutDate: null,
    fsc: true,
    keyword: "",
    noOfAdult: 1,
    noOfRooms: 1,
    noOfChild: 0,
    childAges: [],
    cityChanged: false,
    ...(queryparams ? queryparams : {}),
  };
  return {
    formSchema,
    onSearch,
    formRef,
    countries,
    initialValues,
    staticData,
    personError,
    setPersonError,
    setSameDateError,
    sameDateError
  };
};
